/**
 * Agents service
 * Handles API requests for agent management
 */

import { api } from '../utils/api-client'
import type { AgentsListResponse, AgentResponse, AgentQueryParams } from '../types/agent-types'

/**
 * Agents service class
 */
export class AgentsService {
  /**
   * Fetches a list of agents for the current workspace
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to agents list response
   */
  static async getAgents(params?: AgentQueryParams): Promise<AgentsListResponse> {
    const searchParams = new URLSearchParams()
    
  
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString())
    }
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString())
    }
    if (params?.published !== undefined) {
      searchParams.append('published', params.published.toString())
    }
    if (params?.search) {
      searchParams.append('search', params.search)
    }
    if (params?.appId) {
      searchParams.append('appId', params.appId)
    }

    const queryString = searchParams.toString()
    const url = queryString ? `/agents?${queryString}` : '/agents'
    
    return api.get(url, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    }) 
  }
  
  /**
   * Fetches a single agent by ID
   * @param agentId - Agent UUID
   * @returns Promise resolving to agent response
   */
  static async getAgent(
    agentId: string,
    options?: { showErrorToast?: boolean }
  ): Promise<AgentResponse> {
    return api.get(`/agents/${agentId}`, {
      requiresAuth: true,
      // Callers that render their own not-found / no-access UI (e.g. the
      // standalone agent page) opt out so the failed request doesn't also fire
      // a redundant error toast.
      showErrorToast: options?.showErrorToast ?? true,
      showSuccessToast: false,
    })
  }
  
  /**
   * Fetches published agents only
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to published agents list
   */
  static async getPublishedAgents(params?: Omit<AgentQueryParams, 'published'>): Promise<AgentsListResponse> {
    return this.getAgents({
      ...params,
      published: true,
    })
  }
  
  /**
   * Fetches draft agents only
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to draft agents list
   */
  static async getDraftAgents(params?: Omit<AgentQueryParams, 'published'>): Promise<AgentsListResponse> {
    return this.getAgents({
      ...params,
      published: false,
    })
  }

  /**
   * Updates an agent with partial data
   * @param agentId - Agent UUID
   * @param data - Partial agent data to update
   * @returns Promise resolving to agent response
   */
  static async updateAgent(
    agentId: string,
    data: {
      published?: boolean;
      agentSettings?: {
        preferredApiKeyId?: string | null;
        [key: string]: any;
      };
    }
  ): Promise<AgentResponse> {
    return api.patch(`/agents/${agentId}`, data, {
      requiresAuth: true,
      showErrorToast: true,
      showSuccessToast: false,
    })
  }
}

/**
 * Convenience instance of the agents service
 */
export const agentsService = {
  getAgents: AgentsService.getAgents,
  getAgent: AgentsService.getAgent,
  getPublishedAgents: AgentsService.getPublishedAgents,
  getDraftAgents: AgentsService.getDraftAgents,
  updateAgent: AgentsService.updateAgent,
}

/**
 * Export default for easier imports
 */
